import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import { CloseCircleIcon } from "icons/payments/CloseCircleIcon";
import { AddCircleIcon } from "icons/payments/AddCircleIcon";
import { ArrowUpIcon } from "icons/payments/ArrowUpIcon";
import { ArrowDownIcon } from "icons/payments/ArrowDownIcon";
import { ArrowReplyIcon } from "icons/payments/ArrowReplyIcon";
import InputField from "components/form/InputField";
import Button from "components/form/Button";
import { CalenderIcon, DownArrow, UpArrow } from "utils/icon";
import CustomCalender from "components/calender/CustomCalender";
import { useOutsideClick } from "utils/HelperFunctions";
import { getShellZoom } from "utils/shellZoom";

interface Option {
  name: string;
  value: string;
}

interface FilterProps {
  setIsOpenDropDown: any;
  openRef: any;
  filterData: Option[];
  dayNumber: string;
  setAmtVal: Dispatch<SetStateAction<string>>;
  setIsOpenTimeOption: Dispatch<SetStateAction<boolean>>;
  isOpenTimeOption: boolean;
  selectedValue: string;
  setSelectedValue: Dispatch<SetStateAction<string>>;
  setIsOpenCalender: Dispatch<SetStateAction<boolean>>;
  isOpenCalender: boolean;
  formattedDate: string;
  dateVal: Date;
  setDateVal: Dispatch<SetStateAction<Date>>;
  setShowLabel: Dispatch<SetStateAction<string>>;
  setdateFilter: any;
  isNextPayment: boolean;
}

const TimeOption = ["days", "months", "hours"];

const DateFilter = (props: any) => {
  const { label = "label", options, setdateFilter, open = () => {} } = props;
  const [isOpenDropDown, setIsOpenDropDown] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState("created-date");

  const [dayNumber, setAmtVal] = useState<string>("");
  const [isOpenCalender, setIsOpenCalender] = useState<boolean>(false);
  const [dateVal, setDateVal] = useState<Date>(new Date());
  const formattedDate = moment(dateVal).format("MM/DD/YYYY");
  const [selectedValue, setSelectedValue] = useState("days");
  const [isOpenTimeOption, setIsOpenTimeOption] = useState<boolean>(false);
  const [showLabel, setShowLabel] = useState("");
  const [filterData, setFilterData] = useState<Option[]>(options);
  const dateDropDownRef = useRef<HTMLDivElement>(null);
  const portalDropdownRef = useRef<HTMLDivElement>(null);
  const childDropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 350 });

  const updateDropdownPosition = () => {
    if (!dateDropDownRef.current) return;
    // The dropdown is portalled and scaled, so it reads its own inline
    // coordinates as design px while the measured rect is real px. Dividing by
    // the shell's factor puts both in the same space; it is 1 when unscaled.
    const zoom = getShellZoom(dateDropDownRef.current);
    const rect = dateDropDownRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth / zoom;
    const viewportPadding = 12;
    const dropdownWidth = Math.min(350, viewportWidth - viewportPadding * 2);
    const preferredLeft = rect.left / zoom;
    const maxLeft = viewportWidth - dropdownWidth - viewportPadding;
    setDropdownPos({
      top: rect.bottom / zoom + 5,
      left: Math.max(viewportPadding, Math.min(preferredLeft, maxLeft)),
      width: dropdownWidth,
    });
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dateDropDownRef.current &&
        !dateDropDownRef.current.contains(e.target as Node) &&
        (!portalDropdownRef.current ||
          !portalDropdownRef.current.contains(e.target as Node))
      ) {
        setIsOpenDropDown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpenDropDown) return;
    const handleResize = () => updateDropdownPosition();
    const handleScroll = () => setIsOpenDropDown(false);
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpenDropDown]);

  useEffect(() => {
    setFilterData(options);
  }, [options]);

  useEffect(() => {
    setDateVal(new Date());
  }, []);

  // Shared by the click and keyboard handlers so both paths behave identically.
  const handleTriggerToggle = () => {
    if (!isOpenDropDown) {
      open();
      setTimeout(() => {
        updateDropdownPosition();
        setIsOpenDropDown(true);
      }, 350);
    } else {
      setIsOpenDropDown(false);
    }
  };

  const clearDateFilter = () => {
    setdateFilter({});
    setShowLabel("");
    setAmtVal("");
  };

  return (
    <div className="relative w-full" ref={dateDropDownRef}>
      {/* Same keyboard treatment as the Amount/Status/Type filters. Without it
          this chip had no focusable element at all, so Tab jumped straight past
          Date to Amount and a keyboard-only customer could not filter by date. */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isOpenDropDown}
        aria-haspopup="dialog"
        aria-label={`${label} filter`}
        className="rounded-[20px] w-full hover:bg-bgHover border-[1px] border-strokePrimary bg-bgPrimary h-[34px] flex items-center gap-[6px] pl-[9px] pr-[15px] cursor-pointer "
        onClick={handleTriggerToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleTriggerToggle();
          }
        }}
      >
        <div
          role={showLabel ? "button" : undefined}
          tabIndex={showLabel ? 0 : undefined}
          aria-label={showLabel ? `Clear ${label} filter` : undefined}
          className="cursor-pointer"
          onClick={(e) => {
            if (showLabel) {
              e.stopPropagation();
              e.preventDefault();
              clearDateFilter();
            }
          }}
          onKeyDown={(e) => {
            if (showLabel && (e.key === "Enter" || e.key === " ")) {
              e.stopPropagation();
              e.preventDefault();
              clearDateFilter();
            }
          }}
        >
          {showLabel ? <CloseCircleIcon /> : <AddCircleIcon />}
        </div>
        <div className="text-txtParagraph leading-normal font-medium text-xs whitespace-nowrap">
          {label}
        </div>
        {showLabel && <div className="h-[16px] w-[1px] bg-txtParagraph" />}
        {showLabel && (
          <div className="text-sm font-bold text-txtPrimary leading-normal">
            {showLabel}
          </div>
        )}
      </div>

      {isOpenDropDown &&
        ReactDOM.createPortal(
          <div
            ref={portalDropdownRef}
            style={{
              position: "fixed",
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 9999,
            }}
            className="scaled-portal px-[15px] bg-bgPrimary shadow-[0px_0px_12px_-3px_#DFE4EC] rounded-[7px]"
          >
            <div className="w-full flex flex-col ">
              <div className="flex gap-[9px] py-[15px]">
                <input
                  className="w-[20px] h-[20px] focus-visible:outline-none cursor-pointer"
                  type="radio"
                  id={"created-date"}
                  value={"created-date"}
                  checked={selectedOption === "created-date"}
                  onChange={() => setSelectedOption("created-date")}
                />
                <label
                  htmlFor={"created-date"}
                  className={`text-base  leading-[20.83px] capitalize ${
                    selectedOption === "created-date"
                      ? "text-txtPrimary"
                      : "text-txtSecondary"
                  }`}
                >
                  Created date
                </label>
              </div>

              {selectedOption === "created-date" && (
                <>
                  <Filter
                    filterData={filterData}
                    openRef={childDropdownRef}
                    setIsOpenDropDown={setIsOpenDropDown}
                    dayNumber={dayNumber}
                    setAmtVal={setAmtVal}
                    setIsOpenTimeOption={setIsOpenTimeOption}
                    isOpenTimeOption={isOpenTimeOption}
                    selectedValue={selectedValue}
                    setSelectedValue={setSelectedValue}
                    setIsOpenCalender={setIsOpenCalender}
                    isOpenCalender={isOpenCalender}
                    formattedDate={formattedDate}
                    dateVal={dateVal}
                    setDateVal={setDateVal}
                    setShowLabel={setShowLabel}
                    setdateFilter={setdateFilter}
                    isNextPayment={
                      selectedOption === "created-date" ? false : true
                    }
                  />
                </>
              )}

              <div className="flex gap-[9px] py-[15px]">
                <input
                  className="w-[20px] h-[20px] focus-visible:outline-none cursor-pointer"
                  type="radio"
                  id={"next-payment"}
                  value={"next-payment"}
                  checked={selectedOption === "next-payment"}
                  onChange={() => setSelectedOption("next-payment")}
                />
                <label
                  htmlFor={"next-payment"}
                  className={`text-base  leading-[20.83px] capitalize ${
                    selectedOption === "next-payment"
                      ? "text-txtPrimary"
                      : "text-txtSecondary"
                  }`}
                >
                  Next Payment
                </label>
              </div>

              {selectedOption === "next-payment" && (
                <Filter
                  filterData={filterData}
                  openRef={childDropdownRef}
                  setIsOpenDropDown={setIsOpenDropDown}
                  dayNumber={dayNumber}
                  setAmtVal={setAmtVal}
                  setIsOpenTimeOption={setIsOpenTimeOption}
                  isOpenTimeOption={isOpenTimeOption}
                  selectedValue={selectedValue}
                  setSelectedValue={setSelectedValue}
                  setIsOpenCalender={setIsOpenCalender}
                  isOpenCalender={isOpenCalender}
                  formattedDate={formattedDate}
                  dateVal={dateVal}
                  setDateVal={setDateVal}
                  setShowLabel={setShowLabel}
                  setdateFilter={setdateFilter}
                  isNextPayment={
                    selectedOption === "next-payment" ? true : false
                  }
                />
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DateFilter;

const Filter: React.FC<FilterProps> = (props) => {
  const {
    openRef,
    filterData,
    setIsOpenDropDown,
    dayNumber,
    setAmtVal,
    setIsOpenTimeOption,
    isOpenTimeOption,
    selectedValue,
    setSelectedValue,
    setIsOpenCalender,
    isOpenCalender,
    formattedDate,
    dateVal,
    setDateVal,
    setShowLabel,
    setdateFilter,
    isNextPayment,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isBetweenStart, setIsBetweenStart] = useState<boolean>(false);
  const [isBetweenEnd, setIsBetweenEnd] = useState<boolean>(false);
  const [select, setSelect] = useState<string>("is in the last");
  const [betweenStart, setBetweenStart] = useState<Date>(new Date());
  const [betweenEnd, setBetweenEnd] = useState<Date>(new Date());
  const calenderBetweenStartRef = useRef<any>();
  const calenderBetweenEndRef = useRef<any>();
  const calenderRef = useRef<any>();
  useOutsideClick(calenderRef, () => setIsOpenCalender(false));
  useOutsideClick(calenderBetweenStartRef, () => setIsBetweenStart(false));
  useOutsideClick(calenderBetweenEndRef, () => setIsBetweenEnd(false));

  const handleFilterValue = () => {
    let filterObject: any = {
      source: isNextPayment ? "next_payment" : "created_date",
    };

    let keyName = filterData?.find((x) => x.name === select);
    const key = keyName?.value ?? "defaultKey";

    if (key !== "defaultKey") {
      filterObject.period = keyName?.name;
    }

    if (select === "is in the last") {
      if (dayNumber) filterObject.numberOf = dayNumber;
      if (selectedValue) filterObject.duration = selectedValue;
      setShowLabel(`In last ${dayNumber} ${selectedValue || "days"}`);
    } else if (
      select === "is equal to" ||
      select === "is on or after" ||
      select === "is before"
    ) {
      filterObject.date = moment(dateVal).format("YYYY-MM-DD HH:mm:ss");
      setShowLabel(`${moment(dateVal).format("YYYY-MM-DD")}`);
    } else if (select === "is between") {
      filterObject.startDate = moment(betweenStart).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      filterObject.endDate = moment(betweenEnd).format("YYYY-MM-DD HH:mm:ss");
      setShowLabel(
        `${moment(betweenStart).format("YYYY-MM-DD")} to ${moment(
          betweenEnd
        ).format("YYYY-MM-DD")}`
      );
    }
    setdateFilter(filterObject);
    setIsOpenDropDown(false);
  };

  const handleStatusChange = (e: any, isCheckbox: boolean, c: string) => {
    if (isCheckbox) {
      setSelect(c);
      setIsOpen(false);
    } else {
      if (select && select === c) {
        setSelect("");
      } else {
        setSelect(c);
        setIsOpen(false);
      }
    }
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <>
      <div className="py-[11px] px-[15px]">
        <div
          className="w-full px-3 py-[6px] bg-bgPrimary border border-strokePrimary hover:!border-strokeActive  rounded flex justify-between items-center relative"
          ref={openRef}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="text-sm font-normal leading-normal text-txtPrimary">
            {select}
          </div>
          <div
            className="flex flex-col gap-[-2px] cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
          >
            <ArrowUpIcon />
            <ArrowDownIcon />
          </div>
          {isOpen ? (
            <div className="absolute bg-bgPrimary shadow-[0px_0px_12px_-3px_#DFE4EC] z-40 rounded-[7px] w-full top-[43px] left-0">
              <div className={`max-h-[200px] overflow-y-auto`}>
                {filterData?.map((data: any, i: number) => {
                  return (
                    <div
                      key={i}
                      onClick={(e) => handleStatusChange(e, true, data.name)}
                    >
                      <div className="flex items-center gap-[7px] h-[40px] px-[15px] py-[11px] leading-normal border-b border-b-strokePrimary hover:bg-bgHover">
                        <input
                          type="checkbox"
                          checked={select === data?.name}
                          onChange={(e) => {
                            handleStatusChange(e, true, data.name);
                          }}
                          className="dcdial_portal_checkbox"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        />
                        <div
                          className="text-txtPrimary text-sm font-normal cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(e, true, data.name);
                          }}
                        >
                          {data?.name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {select ? (
        <div className="pb-[11px] px-[15px] flex items-center gap-3 animate__animated animate__fadeIn w-">
          {select === "is in the last" && (
            <div className="flex items-center gap-3 w-full sm:flex-nowrap flex-wrap">
              <ArrowReplyIcon className="" />
              <InputField
                name="dayNumber"
                value={dayNumber}
                showLabel={false}
                placeholder="5"
                type="number"
                className="!px-3 !py-[6px] sm:!w-[100px] !w-[95%]"
                onChange={(e) => setAmtVal(e.target.value)}
              />
              <div
                className="relative h-[38px] grow border rounded px-[12px] flex gap-2 items-center text-sm justify-between cursor-pointer"
                onClick={() => setIsOpenTimeOption(!isOpenTimeOption)}
              >
                {selectedValue}
                <div>
                  <UpArrow />
                  <DownArrow />
                </div>
                {isOpenTimeOption && (
                  <div
                    className="absolute bg-white rounded border w-full top-[38px] left-0"
                    ref={calenderRef}
                  >
                    {TimeOption.map((option, index) => (
                      <div
                        key={index}
                        className="px-[15px] py-[6px] cursor-pointer hover:bg-[#f3f4f8] text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedValue(option);
                          setIsOpenTimeOption(false);
                        }}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {(select === "is equal to" ||
            select === "is on or after" ||
            select === "is before") && (
            <div
              className="relative flex items-center gap-[15px]"
              ref={calenderRef}
            >
              <ArrowReplyIcon />

              <div
                className=" cursor-pointer  w-[135px] h-[40px] border rounded   text-sm flex  items-center justify-between  border-strokePrimary  px-[11px]"
                onClick={() => setIsOpenCalender(!isOpenCalender)}
              >
                <p>{formattedDate}</p>
                <CalenderIcon
                  onClick={() => setIsOpenCalender(!isOpenCalender)}
                />
              </div>

              {isOpenCalender && (
                <CustomCalender
                  isDashFormat={true}
                  value={dateVal}
                  disableTiles={false}
                  onChange={(newDate: any) => setDateVal(newDate)}
                  className=" absolute w-[268px]"
                  openCalendar={isOpenCalender}
                  setOpenCalendar={setIsOpenCalender}
                />
              )}
            </div>
          )}
          {select === "is between" && (
            <div className="flex justify-between items-center w-full">
              <div
                className="relative cursor-pointer  w-[135px] h-[40px] border rounded flex items-center  border-strokePrimary "
                ref={calenderBetweenStartRef}
              >
                <div
                  className="  text-sm flex  items-center justify-between w-full px-[11px]"
                  onClick={() => setIsBetweenStart(!isBetweenStart)}
                >
                  <p>{moment(betweenStart).format("MM/DD/YYYY")}</p>
                  <CalenderIcon
                    onClick={() => setIsBetweenStart(!isBetweenStart)}
                  />
                </div>

                {isBetweenStart && (
                  <CustomCalender
                    isDashFormat={true}
                    value={betweenStart}
                    disableTiles={false}
                    onChange={(newDate: any) => setBetweenStart(newDate)}
                    className="absolute w-[268px] left-[-65px] top-[42px]"
                    openCalendar={isOpenCalender}
                    setOpenCalendar={setIsOpenCalender}
                  />
                )}
              </div>
              -
              <div
                className="relative cursor-pointer  w-[135px] h-[40px] border rounded flex items-center  border-strokePrimary "
                ref={calenderBetweenEndRef}
              >
                <div
                  className="  text-sm flex  items-center justify-between w-full px-[11px]"
                  onClick={() => setIsBetweenEnd(!isBetweenEnd)}
                >
                  <p>{moment(betweenEnd).format("MM/DD/YYYY")}</p>
                  <CalenderIcon
                    onClick={() => setIsBetweenEnd(!isBetweenEnd)}
                  />
                </div>

                {isBetweenEnd && (
                  <CustomCalender
                    isDashFormat={true}
                    value={betweenEnd}
                    disableTiles={false}
                    onChange={(newDate: any) => setBetweenEnd(newDate)}
                    className=" absolute w-[268px] left-[-63px] top-[42px]"
                    openCalendar={isOpenCalender}
                    setOpenCalendar={setIsOpenCalender}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      ) : null}

      <div className="py-[11px] px-[15px]">
        <Button
          text="Apply"
          className="w-full"
          isDisabled={
            select === "is equal to" ||
            select === "is on or after" ||
            select === "is before" ||
            select === "is between"
              ? false
              : select === "is in the last" && dayNumber
                ? false
                : true
          }
          onClick={() => handleFilterValue()}
        />
      </div>
    </>
  );
};
